import { copyLabels, dereference, updateLabel } from "../tracking/FeatureTracking";
import { forEachElement, toArray } from "../util/Utils";
import { eventQBus } from "../types/EventQBus";
import { events } from "../events";
import { flushPreselected } from "@otto-ec/heureka-components/facet";
import { SortingForm } from "../sorting/SortingForm";
import { isActive } from "../toggle/Toggle";
import { readTsFeatureFilterMethod } from "./FilterTracking";

export const CLASS_JS_FORCE_FORCE_SUBMIT = "js_forceFormSubmit";
export const CLASS_HC_TS_FORCE_FORCE_SUBMIT = "ts_hc_forceFormSubmit";

const TS_DESELECT_FORM = ".ts_heureka_deselectForm";
const JS_CLEAR_FORM = ".js_clearForm";
const JS_CLEAR_INPUT = ".js_clearInput";
const JS_CLEAR_ALL_INPUTS = ".js_clearAllInputs";

export type FilterSubmitEvent = CustomEvent<{
  submitter: HTMLFormElement | HTMLInputElement | HTMLSelectElement;
}>;

function createFilterSubmitEvent(submitter: HTMLFormElement | HTMLInputElement | HTMLSelectElement): FilterSubmitEvent {
  return new CustomEvent("filterSubmit", {
    bubbles: true,
    cancelable: true,
    detail: {
      submitter: submitter,
    },
  });
}

export function isForceSubmit(form: HTMLFormElement) {
  return (
    form.classList.contains(CLASS_JS_FORCE_FORCE_SUBMIT) || form.classList.contains(CLASS_HC_TS_FORCE_FORCE_SUBMIT)
  );
}

/**
 *
 *
 *
 *
 */
export function fireFilterSubmit(target: HTMLFormElement | HTMLInputElement | HTMLSelectElement): boolean {
  const form = target instanceof HTMLFormElement ? target : target.form;
  if (!form) {
    /*                                                                              */
    return false;
  }
  const filterEvent = createFilterSubmitEvent(target);

  const preventSubmission = !isForceSubmit(form) && !hasChanges(form);

  if (preventSubmission) {
    filterEvent.preventDefault();
  }

  form.dispatchEvent(filterEvent);

  return !preventSubmission;
}

function isDefaultCheckedChanged(input: HTMLInputElement): boolean {
  /*                                                                           */
  /*                                                                              */
  /*                                         */
  const defaultChecked = input.dataset.defaultChecked === "true";
  return input.dataset.defaultChecked ? input.checked !== defaultChecked : input.checked !== input.defaultChecked;
}

/**
 *
 *
 *
 *
 */
export function isChanged(elem?: Element | HTMLInputElement) {
  if (elem && elem.getAttribute("type")) {
    /*                                                                                                                                     */
    const input = elem as HTMLInputElement;
    switch (elem.getAttribute("type")) {
      case "text":
        return input.value !== input.defaultValue;

      case "radio":
      case "checkbox":
        return isDefaultCheckedChanged(input) || input.value !== input.defaultValue;

      default:
        return false;
    }
  }
  return false;
}

/**
 *
 *
 *
 *
 */
export function hasChanges(form: HTMLFormElement) {
  return !!Array.from(form.elements).filter(isChanged).length;
}

/**
 *
 *
 */
export function clearFormInput(elem: HTMLInputElement): HTMLInputElement {
  const type = elem.getAttribute("type");
  if (type) {
    switch (type.toLowerCase()) {
      case "text":
      case "tel":
      case "number":
        elem.value = "";
        break;
      case "radio":
      case "checkbox":
        if (elem.checked) {
          elem.checked = false;
        }
        break;
    }
  }
  return elem;
}

/**
 *
 *
 *
 */
export function deselectForm(event: Event) {
  const target = event.target as HTMLInputElement;
  const form = target.form;
  if (form) {
    const filterMethod = readTsFeatureFilterMethod(target, false);
    toArray<HTMLInputElement>(form.elements)
      .map(clearFormInput) /*                                 */
      /*                                   */
      .filter(isChanged)
      /*                                                  */
      .forEach((item) => updateLabel(item, "san_FilterMethod", filterMethod));
  }
  form?.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 *
 *
 *
 */
export function clearAndSubmit(event: Event) {
  const target = event.target as HTMLInputElement;
  const form = target.form;

  if (form) {
    toArray<HTMLInputElement>(form.elements)
      .map(clearFormInput) /*                                 */
      /*                                   */
      .filter(isChanged)
      /*                                                  */
      .forEach((item) => copyLabels(target, item));
  }

  /*     */
  fireFilterSubmit(target);
}

/**
 *
 *
 *
 *
 *
 *
 */
function clearInput(this: HTMLInputElement, event: Event | { currentTarget: HTMLInputElement }) {
  const target = event.currentTarget as HTMLInputElement;

  /*                                                 */
  copyLabels(this, target);

  const input = dereference(target);
  if (input) {
    clearFormInput(input);
  }
}

/**
 *
 *
 *
 */
export function clearAllInputs(event: Event) {
  const target = event.target as HTMLInputElement;

  forEachElement<HTMLInputElement>(JS_CLEAR_INPUT, (elem) =>
    clearInput.call(target, {
      currentTarget: elem,
    }),
  );
}

/**
 *
 *
 *
 *
 *
 */
export function interceptSubmit(event: SubmitEvent) {
  if (event.defaultPrevented) {
    return;
  }

  fireFilterSubmit(event.target as HTMLFormElement);

  /*                         */
  event.preventDefault();
}

/**
 *
 *
 *
 */
export function unlockSubmitButtons(this: HTMLFormElement) {
  forEachElement("[type='submit'][disabled]", (input) => input.removeAttribute("disabled"), this);
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function create(form: HTMLFormElement) {
  forEachElement(TS_DESELECT_FORM, (input) => input.addEventListener("click", deselectForm), form);
  forEachElement(JS_CLEAR_FORM, (input) => input.addEventListener("click", clearAndSubmit), form);
  forEachElement(JS_CLEAR_INPUT, (input) => input.addEventListener("click", clearInput), form);
  forEachElement(JS_CLEAR_ALL_INPUTS, (input) => input.addEventListener("click", clearAllInputs), form);

  form.addEventListener("submit", interceptSubmit);
  form.addEventListener("input", unlockSubmitButtons, { passive: true, once: true });
}

/**
 *
 *
 */
function submitFiltersNative(event: Event) {
  if (event.defaultPrevented) {
    return;
  }

  const form = event.target as HTMLFormElement;

  /*                                                             */
  /*                                                                                    */
  setTimeout(function () {
    form.submit();
  }, 0);
}

function hasZeroHitsOnAllEntries(formData: FormData, form: HTMLFormElement, excludedKeys: string[]) {
  /*                                                   */
  if (!form.querySelector(".heureka_facetValue__dangerousZeroHits")) {
    return false;
  }
  let totalEntriesCount = 0,
    dangerousZeroHitsCount = 0;
  formData.forEach((value, key) => {
    if (!excludedKeys.includes(key)) {
      if (form.querySelector(`input[name='${key}'][value='${value}'].heureka_facetValue__dangerousZeroHits`)) {
        dangerousZeroHitsCount++;
      }
      totalEntriesCount++;
    }
  });

  return dangerousZeroHitsCount > 0 && totalEntriesCount == dangerousZeroHitsCount;
}

function appendDeliveryStatementValuesTo(formData: FormData) {
  /*                                                                                      */
  const value = formData.get("lieferaussage");
  if (value && typeof value === "string") {
    const values = value.split(".");
    formData.delete("lieferaussage");
    values.forEach((value) => {
      formData.append("lieferaussage", value);
    });
  }
}

/**
 *
 *
 *
 */
function submitFiltersAsync(event: FilterSubmitEvent) {
  if (event.defaultPrevented) {
    return;
  }

  /*                                          */
  /*                                       */
  /*                          */
  /*                                                                                                  */
  /*                    */
  if (!event.detail || !FormData.prototype.entries) {
    submitFiltersNative(event);
    return;
  }

  const form = <HTMLFormElement>event.target;
  const formData = new FormData(form);
  /*                                                                                          */
  const excludedKeys: string[] = ["sortiertnach", "rule", "group", "lieferaussage"];
  if (hasZeroHitsOnAllEntries(formData, form, excludedKeys)) {
    formData.forEach((value, key) => {
      if (!excludedKeys.includes(key)) {
        formData.delete(key);
      }
    });
  }

  appendDeliveryStatementValuesTo(formData);

  /*                                                                         */
  /*                                                                                  */
  /*                                                                            */
  /*                                                                                                   */
  if (isActive("HEUREKA_2113_SORTING_SELECT_CHIP")) {
    const currentSortiertnach = SortingForm.getCurrentSortValue();
    if (currentSortiertnach) {
      formData.set("sortiertnach", currentSortiertnach);
    }
  }

  /*                                                                                                        */
  /*                                                             */
  /*                                                                                    */
  setTimeout(function () {
    eventQBus.emit("ftfind.dresonRule.resolve", { formData: formData });
  }, 0);
}

/**
 *
 *
 *
 */
function updateLayoutField(event: { page?: { l?: string } } | null | undefined) {
  const { page } = event || {};
  const { l = "" } = page || {};

  forEachElement<HTMLInputElement>(".js_formSorting [name='l'][type='hidden']", (input) => (input.value = l));
}

/**
 *
 */
export function registerFilterFormListeners(rootElement?: ParentNode | null) {
  forEachElement("form.ts_formFilter", create, rootElement);
}

function registerListeners() {
  registerFilterFormListeners();
}

function emptyPreselected() {
  flushPreselected();
}

export function reinitializePaliElements(baseSelector: string) {
  eventQBus.emit("assets.accordion.init", baseSelector);
  eventQBus.emit("assets.dropdown.init", baseSelector);
  eventQBus.emit("assets.label.init", baseSelector);
  eventQBus.emit("assets.formular.init", baseSelector);
  eventQBus.emit("assets.tooltip.init", baseSelector);
}

function reinitializePaliElementsInFilterSection() {
  reinitializePaliElements("#find_filterSection");
  reinitializePaliElements(".heureka_filterButtons");
  /*                                                                                                                                                                                   */
  events.slidersInitialized();
}

/**
 *
 *
 */
export function initializeLayoutField() {
  const datasetElement = document.querySelector(".js_pagingLink") as HTMLElement;
  if (datasetElement) {
    const page = datasetElement.dataset["page"];
    if (page) {
      forEachElement<HTMLInputElement>(
        ".js_formSorting [name='l'][type='hidden']",
        (input) => (input.value = JSON.parse(page)["l"]),
      );
    }
  }
}

export default function registerFilterFormActionsHandlers() {
  document.addEventListener("filterSubmit", submitFiltersAsync as EventListener);
  eventQBus.on("ftfind.tilelist.loaded", updateLayoutField);
  events.onFilterSectionLoaded(reinitializePaliElementsInFilterSection);
  events.onFilterSectionLoaded(registerListeners);
  events.onFilterSectionLoaded(emptyPreselected);
}
